import {
  COPY_ROW_DATA,
  editRowData,
  errorSaveData,
  fetchDataFailure,
  fetchDataStart,
  fetchDataSuccess,
  getPlanAdminSplitDollarList,
  RESET_SAVE_DATA_RESPONSE,
  resetSaveDataResponseState,
  saveData,
  TRIGGER_EDIT_PLAN_ADMIN_SPLIT_DOLLAR_CELL,
  TRIGGER_PLAN_ADMIN_SPLIT_DOLLAR_LIST_REQUEST,
  TRIGGER_SAVE_PLAN_ADMIN_SPLIT_DOLLAR,
  TRIGGER_SET_FINANCING_EXIT_PLAN_ADMIN_SPLIT_DOLLAR,
  updateCopiedData,
} from "app/redux/features/plan-admin-split-dollar";
import {
  AdminSplitDollarActionPayload,
  EditCellAdminSplitDollarActionPayload,
  FinancialInfoActionPayload,
  SetFinancingExitAdminSplitDollarActionPayload,
} from "app/utils/types";
import { makeRequest } from "app/utils/makeRequest";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { applyFinancingExitChange, prepareRows } from "app/Data/PlanAdminSplitDollarDataJson";
import { getPolicyInputBaseCSVData } from "app/redux/features/policy-input-base-csv-data";
import { clone } from "ramda";
import { getCreateProposalContactInfo } from "app/redux/features/createProposal";

function* planAdminSplitDollarListRequest(
  actions: FinancialInfoActionPayload,
): Generator<any, void, any> {
  try {
    const {
      payload: { address: path, key, requestParams },
    } = actions;
    yield put(fetchDataStart());
    const response = yield call(() => makeRequest(path).get(key, requestParams));
    yield put(fetchDataSuccess(response.data));
  } catch (error: any) {
    yield put(fetchDataFailure(error));
  }
}

function* savePlanAdminSplitDollarData(
  actions: AdminSplitDollarActionPayload,
): Generator<any, void, any> {
  const store = yield select(getPlanAdminSplitDollarList);
  try {
    const {
      payload: { address: path, body },
    } = actions;
    const response = yield call(() =>
      makeRequest(path).put("", {
        ...body,
        planAdminSDRows: JSON.stringify(store.data?.planAdminSDData?.rows ?? []),
        planSDRows: JSON.stringify(store.newSDDataArr ?? []),
      }),
    );
    yield put(saveData(response.data));
  } catch (error: any) {
    yield put(errorSaveData(error));
  }
}

function* editCellPlanAdminSplitDollar(
  actions: EditCellAdminSplitDollarActionPayload,
): Generator<any, void, any> {
  const {
    payload: { key, rowIdx, value },
  } = actions;
  try {
    const store = yield select(getPlanAdminSplitDollarList);
    const policyInputCSVData = yield select(getPolicyInputBaseCSVData);
    const createProposalContactData = yield select(getCreateProposalContactInfo);

    // `accrueInterest` lives on the Other Details ("Others") tab and decides whether the
    // employee pays the plan interest or it accrues into the loan — which in turn drives every
    // AFR-derived column: minimum AFR interest, imputed compensation and the BOY/EOY balances.
    // Its placeholder value in the store is "", which is falsy, so recalculating before that
    // tab had loaded silently rebuilt every row as though interest were NOT accruing, and the
    // draft save then persisted those numbers. That is why AFR figures kept drifting without
    // anyone touching the Others tab. The modal now fetches Other Details on mount and keeps
    // the grid read-only until it lands, so by the time an edit gets here the value is real.
    // An unset value still means "No", as before.
    const accrueInterest =
      createProposalContactData?.createProposalOtherDetailsSuccess?.data?.data?.accrueInterest ===
      true;

    const { splitDollarRowArr, updatedRows } = prepareRows(
      clone(store.data?.planAdminSDData?.rows),
      key,
      value,
      rowIdx,
      store.data.planSDSchema,
      policyInputCSVData.data?.rows,
      accrueInterest,
    );

    yield put(
      editRowData({
        rowIdx,
        splitDollarRowArr,
        updatedRows,
      }),
    );
  } catch (error: any) {
    // A failed recalculation must not leave a half-updated row behind silently.
    yield put(
      errorSaveData({
        message:
          error?.message ?? "The row could not be recalculated. Please reload and try again.",
      }),
    );
  }
}

function* setFinancingExitPlanAdminSplitDollar(
  actions: SetFinancingExitAdminSplitDollarActionPayload,
): Generator<any, void, any> {
  const {
    payload: { rowIdx },
  } = actions;
  try {
    const store = yield select(getPlanAdminSplitDollarList);
    const createProposalContactData = yield select(getCreateProposalContactInfo);

    // See the comment in editCellPlanAdminSplitDollar: accrueInterest must come from
    // Other Details, not a placeholder, or every AFR-derived column recalculates wrong.
    const accrueInterest =
      createProposalContactData?.createProposalOtherDetailsSuccess?.data?.data?.accrueInterest ===
      true;

    const { splitDollarRowArr, updatedRows } = applyFinancingExitChange(
      clone(store.data?.planAdminSDData?.rows),
      rowIdx,
      store.data.planSDSchema,
      accrueInterest,
    );

    yield put(
      editRowData({
        rowIdx: 0,
        splitDollarRowArr,
        updatedRows,
      }),
    );
  } catch (error: any) {
    yield put(
      errorSaveData({
        message:
          error?.message ??
          "The financing exit selection could not be applied. Please reload and try again.",
      }),
    );
  }
}

function* resetSaveDataState() {
  yield put(resetSaveDataResponseState());
}

function* copyRowData(actions: {
  type: string;
  data: { [key: string]: string | number };
}): Generator<any, void, any> {
  yield put(updateCopiedData(actions.data));
}

export default function* planAdminSplitDollarSaga() {
  yield all([
    takeLatest(TRIGGER_PLAN_ADMIN_SPLIT_DOLLAR_LIST_REQUEST, planAdminSplitDollarListRequest),
    takeLatest(TRIGGER_SAVE_PLAN_ADMIN_SPLIT_DOLLAR, savePlanAdminSplitDollarData),
    takeLatest(TRIGGER_EDIT_PLAN_ADMIN_SPLIT_DOLLAR_CELL, editCellPlanAdminSplitDollar),
    takeLatest(
      TRIGGER_SET_FINANCING_EXIT_PLAN_ADMIN_SPLIT_DOLLAR,
      setFinancingExitPlanAdminSplitDollar,
    ),
    takeLatest(RESET_SAVE_DATA_RESPONSE, resetSaveDataState),
    takeLatest(COPY_ROW_DATA, copyRowData),
  ]);
}
